/**
 * @file tab-assigned-sales-rep.tsx
 * @module app/modules/sales-management/admin/user-management/tabs/tab-assigned-sales-rep
 * @description Implements the tab assigned sales rep interface for sales-management operations.
 *
 * @author Rocket Technologies Ltd
 * @copyright 2026 Rocket Technologies Ltd. All rights reserved.
 * @since 1.1.1
 * @version 2.1.2
 */

import { useEffect, useState } from 'react';
import { Button, Card } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { getKey, setKey } from '../../../../../../Global/history';
import Loader from '../../../../../../Global/loader';
import Pagination from '../../../../../../Global/pagination';
import { success } from '../../../../../../Global/toast';
import { masterToast } from '../../../../../../utils/toast';
import { CustomSelectTable } from '../../../../../custom/Select/CustomSelectTable';
import ThreeDotMenu from '../../../../../../_admin/assets/media/svg_uTrade/three-dot.svg';
import { PAGE_LIMIT } from '../../../../../../utils/constants';
import Method from '../../../../../../utils/methods';
import { listAdminAssignedSalesRep } from '../../../../../../utils/storeString';
import APICallService from '../../../../../../api/apiCallService';
import { assistAdmin } from '../../../../../../api/apiEndPoints';
import AddSalesRepresentativeModal from '../../../../../modals/add-sales-representative-modal';

const TabAssignedSalesRep = (props: any) => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [totalRecords, setTotalRecords] = useState(0);
    const [salesRepresentatives, setSalesRepresentatives] = useState<any>([]);
    const [page, setPage] = useState<any>(getKey(listAdminAssignedSalesRep.page) || 1);
    const [pageLimit, setPageLimit] = useState<any>(
        getKey(listAdminAssignedSalesRep.limit) || PAGE_LIMIT
    );
    const [menuId, setMenuId] = useState<number>(-1);
    const [showMenu, setShowMenu] = useState<boolean>(false);
    const [selectedRep, setSelectedRep] = useState<any>(undefined);
    const [showAddModal, setShowAddModal] = useState<boolean>(false);

    const menuOptions = [
        {
            label: (
                <button className="btn btn-link fs-14 fw-600 text-black ms-3 p-4">
                    Edit
                </button>
            ),
            value: 1,
        }
    ];

    useEffect(() => {
        (async () => {
            await fetchInitData(page, pageLimit);
        })();
    }, [props.representativeId]);

    const fetchInitData = async (pageNo: number, limit: number) => {
        setLoading(true);
        const repId = props.representativeId ? props.representativeId : '';
        let params: any = {
            pageNo: pageNo,
            limit: limit,
            sortKey: "createdAt",
            sortOrder: -1,
            needCount: true,
            "role[0]": 2, // Fetching Sales Reps
            "role[1]": 7, // Fetching Activation Reps
            manager: repId // assigned to this manager
        };

        let apiService = new APICallService(
            assistAdmin.list,
            params,
            "",
            "",
            "",
            "",
            "",
            ""
        );
        let response = await apiService.callAPI();
        if (response && response.records) {
            setTotalRecords(response.total);
            const mappedRecords = response.records.map((val: any) => {
                return {
                    ...val,
                    status: val.active ? "Active" : "Inactive"
                };
            });
            setSalesRepresentatives(mappedRecords);
        } else {
            setSalesRepresentatives([]);
        }
        setLoading(false);
    };

    const handleToggleActivation = async (user: any, index: number) => {
        const apiService = new APICallService(
            assistAdmin.status,
            {
                active: `${user.status === 'Active' ? "false" : "true"}`,
            },
            { id: user._id },
            "",
            "",
            "",
            "",
            ""
        );
        const response = await apiService.callAPI();
        if (response) {
            if (user.status === 'Active') {
                success(masterToast.userDeactivated);
            } else {
                success(masterToast.userActivated);
            }
            const temp: any = [...salesRepresentatives];
            temp[index].status = user.status === 'Active' ? 'Inactive' : 'Active';
            setSalesRepresentatives(temp);
        }
    };

    const onMenuOpen = (id: any) => {
        setMenuId(id);
        setShowMenu(true);
    };

    const onMenuClose = () => {
        setMenuId(-1);
        setShowMenu(false);
    };

    const openMenuOnClick = (id: any) => {
        setMenuId(id);
        setShowMenu(true);
    };

    const handleOption = (event: any, data: any) => {
        if (event.value === 1) {
            setSelectedRep(data);
            setShowAddModal(true);
        }
    };

    const handleCurrentPage = async (val: number) => {
        setLoading(true);
        setPage(val);
        setKey(listAdminAssignedSalesRep.page, val);
        await fetchInitData(val, pageLimit);
        setLoading(false);
    };
    const handleNextPage = async (val: number) => {
        setLoading(true);
        setPage(val + 1);
        setKey(listAdminAssignedSalesRep.page, val + 1);
        await fetchInitData(val + 1, pageLimit);
        setLoading(false);
    };
    const handlePreviousPage = async (val: number) => {
        setLoading(true);
        setPage(val - 1);
        setKey(listAdminAssignedSalesRep.page, val - 1);
        await fetchInitData(val - 1, pageLimit);
        setLoading(false);
    };
    const handlePageLimit = async (event: any) => {
        setLoading(true);
        setPage(1);
        setKey(listAdminAssignedSalesRep.page, 1);
        setKey(listAdminAssignedSalesRep.limit, parseInt(event.target.value));
        setPageLimit(parseInt(event.target.value));
        await fetchInitData(1, event.target.value);
        setLoading(false);
    };

    return (
        <>
            <Card className="border border-r10px">
                <Card.Body className="p-0">
                    <div className="table-responsive">
                        <table className="table table-rounded table-row-bordered align-middle gs-7 gy-6 mb-0">
                            <thead>
                                <tr className="fw-bold fs-16 fw-600 text-dark border-bottom align-middle">
                                    <th className="min-w-md-175px min-w-150px">
                                        Rep name
                                    </th>
                                    <th className="min-w-md-200px min-w-125px">
                                        Assigned Customers
                                    </th>
                                    <th className="min-w-md-125px">
                                        Target
                                    </th>
                                    <th className="min-w-md-125px">
                                        Target Period
                                    </th>
                                    <th className="min-w-md-125px text-center">
                                        Status
                                    </th>
                                    <th className="min-w-150px text-end"></th>
                                </tr>
                            </thead>
                            <tbody>
                                {loading ? (
                                    <tr>
                                        <td colSpan={6} className="text-center">
                                            <div className="d-flex justify-content-center m-4">
                                                <Loader loading={loading} />
                                            </div>
                                        </td>
                                    </tr>
                                ) : (
                                    salesRepresentatives.length > 0 ? (
                                        salesRepresentatives.map((val: any, index: number) => {
                                            return (
                                                <tr key={val.id || index}>
                                                    <td className="fs-15 fw-500">
                                                        <div>{val.name}</div>
                                                        <div className="text-muted">{val.email}</div>
                                                    </td>
                                                    <td className="fs-15 fw-500">
                                                        {val?.currentSalesTarget?.totalAssignedCustomers ? <><div>{val?.currentSalesTarget?.totalAssignedCustomers} Customers</div>
                                                            <div className="text-muted fs-14">{val?.currentSalesTarget?.totalOrders ? val?.currentSalesTarget?.totalOrders : 0} Orders</div></> : "NA"}
                                                    </td>
                                                    <td className="fs-15 fw-500">
                                                        <div>Tsh {Method.getGeneralizedAmount(val?.currentSalesTarget?.achieved)} / Tsh {Method.getGeneralizedAmount(val?.currentSalesTarget?.target ? val?.currentSalesTarget?.target : 0)}</div>
                                                    </td>
                                                    <td className="fs-15 fw-500">{Method.getSalesTargetTypesLabel(val?.currentSalesTarget?.type)}</td>
                                                    <td className="fs-15 fw-500">
                                                        <div className="d-flex justify-content-center">
                                                            <label className="form-check form-switch form-check-custom form-check-solid ">
                                                                <input
                                                                    className="form-check-input form-check-success w-50px h-30px "
                                                                    type="checkbox"
                                                                    name="status"
                                                                    onChange={() =>
                                                                        handleToggleActivation(
                                                                            val,
                                                                            index
                                                                        )
                                                                    }
                                                                    defaultChecked={
                                                                        val.status === 'Active'
                                                                    }
                                                                    checked={val.status === 'Active'}
                                                                />
                                                            </label>
                                                        </div>
                                                    </td>
                                                    <td className="text-end">
                                                        <div className="d-flex justify-content-end align-items-center gap-3 text-nowrap">
                                                            <Button
                                                                className="uw-btn uw-btn-primary fs-14 fw-600"
                                                                onClick={() => {
                                                                    let currentScrollY = window.scrollY;
                                                                    setKey(listAdminAssignedSalesRep.scrollPosition, currentScrollY);
                                                                    navigate("/sales-management/admin/user-management/user-details", {
                                                                        state: { id: val._id, roleType: val.role[0]?.type }
                                                                    });
                                                                }}
                                                            >
                                                                View details
                                                            </Button>
                                                            {/* <div className="my-0">
                                                                <CustomSelectTable
                                                                    marginLeft={"-110px"}
                                                                    width={"auto"}
                                                                    placeholder={
                                                                        <img
                                                                            className="img-fluid"
                                                                            width={23}
                                                                            height={7}
                                                                            src={ThreeDotMenu}
                                                                            alt=""
                                                                        />
                                                                    }
                                                                    options={menuOptions}
                                                                    backgroundColor="white"
                                                                    show={showMenu && index === menuId}
                                                                    onMenuClose={onMenuClose}
                                                                    openMenuOnClick={() => openMenuOnClick(index)}
                                                                    onMenuOpen={() => onMenuOpen(index)}
                                                                    onChange={(event: any) => handleOption(event, val)}
                                                                />
                                                            </div> */}
                                                        </div>
                                                    </td>
                                                </tr>
                                            );
                                        })
                                    ) : (
                                        <tr>
                                            <td colSpan={6} className="fs-15 fw-600 text-center">
                                                No Data Found
                                            </td>
                                        </tr>
                                    )
                                )}
                            </tbody>
                        </table>
                    </div>
                </Card.Body>
            </Card>
            {!loading && salesRepresentatives.length > 0 ? (
                <Pagination
                    totalRecords={totalRecords}
                    currentPage={page}
                    handleCurrentPage={handleCurrentPage}
                    handleNextPage={handleNextPage}
                    handlePreviousPage={handlePreviousPage}
                    handlePageLimit={handlePageLimit}
                    pageLimit={pageLimit}
                />
            ) : (
                <></>
            )}

            <AddSalesRepresentativeModal
                show={showAddModal}
                onHide={() => {
                    setShowAddModal(false);
                    setSelectedRep(undefined);
                }}
                editData={selectedRep}
                onSave={() => {
                    fetchInitData(page, pageLimit);
                    setSelectedRep(undefined);
                }}
            />
        </>
    );
};
export default TabAssignedSalesRep;
